// shared/src/prompts/_rss-render.ts
// Shared renderer for the summarized RSS digest fed to the M-stage macro
// analysts as `sentiment.rss` (= refresh_snapshots.payload.computed.rss).
//
// SHAPE (2026-07-08, RssComputedGroup): `{ <category>: { count, latest_items:
// [{title, summary, date}] } }`. Before this, computed.rss carried only
// `latest_titles: string[]`, and the prompts rendered it as
// `JSON.stringify(rss).slice(0, 2000)`. That blob was ~1.8KB so the slice was
// harmless — BUT it also meant the analysts saw titles only (no summary, no
// date). With per-item summaries the blob exceeds 2000 chars, so the blind
// slice WOULD now truncate; this renderer replaces it with a bounded,
// per-item list.
//
// DEFENSIVE: tolerates the legacy `latest_titles: string[]` shape too, so a
// brief that fires in the ≤15-min window before refresh-fetcher redeploys (its
// latest snapshot still legacy) renders titles rather than "(no rss data)".
//
// Fail-loud per feedback_no_fallbacks: empty / unparseable input renders the
// explicit '(no rss data)' sentinel, never a silent partial.

export interface RenderRssOptions {
  /** Summary clip length (chars). Title/date never clipped. Default 180. */
  readonly maxSummaryChars?: number;
  /** Hard cap on total rendered items across all categories. Default 30
   *  (producer caps 5/group × 5 groups = 25; this is a runaway guard). */
  readonly maxItems?: number;
}

const DEFAULT_MAX_SUMMARY = 180;
const DEFAULT_MAX_ITEMS = 30;

interface RenderedItem {
  category: string;
  title:    string;
  summary:  string;
  date:     string;
}

/** Pull renderable items out of one category value, tolerating both the current
 *  {count, latest_items:[{title,summary,date}]} and legacy {count,
 *  latest_titles:[string]} shapes. Unknown shapes yield []. */
function itemsForCategory(category: string, groupVal: unknown): RenderedItem[] {
  if (!groupVal || typeof groupVal !== 'object') return [];
  const out: RenderedItem[] = [];
  const latestItems = (groupVal as { latest_items?: unknown }).latest_items;
  if (Array.isArray(latestItems)) {
    for (const it of latestItems) {
      if (!it || typeof it !== 'object') continue;
      const title = typeof (it as { title?: unknown }).title === 'string' ? (it as { title: string }).title.trim() : '';
      if (!title) continue;
      out.push({
        category,
        title,
        summary: typeof (it as { summary?: unknown }).summary === 'string' ? (it as { summary: string }).summary.trim() : '',
        date:    typeof (it as { date?: unknown }).date === 'string' ? (it as { date: string }).date.trim() : '',
      });
    }
    return out;
  }
  // Legacy shape: latest_titles: string[]
  const latestTitles = (groupVal as { latest_titles?: unknown }).latest_titles;
  if (Array.isArray(latestTitles)) {
    for (const t of latestTitles) {
      if (typeof t === 'string' && t.trim()) out.push({ category, title: t.trim(), summary: '', date: '' });
    }
  }
  return out;
}

/**
 * Render the RSS digest as a bounded, per-item list for an M-stage analyst
 * prompt. One line per item: `[category] <title> (<date>) — <summary>`. Date
 * and summary are omitted when absent. Empty / malformed input returns the
 * explicit '(no rss data)' sentinel.
 */
export function renderRssBlock(rss: unknown, opts: RenderRssOptions = {}): string {
  const maxSummary = opts.maxSummaryChars ?? DEFAULT_MAX_SUMMARY;
  const maxItems = opts.maxItems ?? DEFAULT_MAX_ITEMS;

  if (!rss || typeof rss !== 'object') return '  (no rss data)';

  const rendered: RenderedItem[] = [];
  for (const [category, groupVal] of Object.entries(rss as Record<string, unknown>)) {
    for (const item of itemsForCategory(category, groupVal)) {
      rendered.push(item);
      if (rendered.length >= maxItems) break;
    }
    if (rendered.length >= maxItems) break;
  }

  if (rendered.length === 0) return '  (no rss data)';

  return rendered.map((it) => {
    const date = it.date ? ` (${it.date})` : '';
    const summary = it.summary
      ? ` — ${it.summary.length > maxSummary ? it.summary.slice(0, maxSummary) + '...' : it.summary}`
      : '';
    return `  [${it.category}] ${it.title}${date}${summary}`;
  }).join('\n');
}
